/**
 * Regras puras da fila "Revisão de documentos" (Variação B, G1) e do Kanban
 * "Demandas urgentes". Sem Vue nem Network: tudo testável em unidade.
 */
import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import {
  DEFAULT_UTC_OFFSET,
  formatDayMonthInOffset,
  formatHourMinuteInOffset,
  formatWeekdayShortInOffset
} from '@/utils/date'
import {
  ProfessionalDocumentRejectionReason,
  ProfessionalDocumentStatus
} from '@/types/professional-documents.types'
import type {
  ApproveVersionPayload,
  BulkDecisionResponse,
  RejectVersionPayload,
  ReviewQueueDocumentSummary,
  ReviewQueueProfessional
} from '@/types/professional-documents.types'
import { PreAllocationColumn } from '@/types/qualification-eligibility.types'
import type { PreAllocationCard } from '@/types/qualification-eligibility.types'

const HOUR_MS = 60 * 60 * 1000
export const WAITING_OVERDUE_HOURS = 72

// --- fila ---------------------------------------------------------------------

export interface ReviewQueueEntry {
  professional: ReviewQueueProfessional
  document: ReviewQueueDocumentSummary
}

/** Médico → documentos, na ordem do backend (Novos → Urgentes → demais). Legado não entra (D11). */
export function flattenQueue(
  items: ReviewQueueProfessional[],
  onlyInReview: boolean
): ReviewQueueEntry[] {
  return items.flatMap((professional) =>
    professional.documents
      .filter((document) => !document.isLegacy)
      .filter(
        (document) => !onlyInReview || document.status === ProfessionalDocumentStatus.IN_REVIEW
      )
      .map((document) => ({ professional, document }))
  )
}

export const isInReview = (entry: ReviewQueueEntry): boolean =>
  entry.document.status === ProfessionalDocumentStatus.IN_REVIEW

export function firstInReviewId(sequence: ReviewQueueEntry[]): number | null {
  return (sequence.find(isInReview) ?? sequence[0])?.document.documentId ?? null
}

/**
 * Próximo Em Análise depois de `fromIndex`, dando a volta. `excludeIds` tira os
 * que acabaram de ser decididos (a sequência ainda é a anterior ao refetch).
 */
export function nextInReviewId(
  sequence: ReviewQueueEntry[],
  fromIndex: number,
  excludeIds: number[] = []
): number | null {
  const pick = (list: ReviewQueueEntry[]) =>
    list.find((entry) => isInReview(entry) && !excludeIds.includes(entry.document.documentId))
  return (pick(sequence.slice(fromIndex + 1)) ?? pick(sequence))?.document.documentId ?? null
}

/** Horas inteiras desde `sinceIso` (ISO UTC do backend); `null` sem data ou com data inválida. */
export function waitingHours(sinceIso: string | null, nowMs = Date.now()): number | null {
  if (!sinceIso) return null
  const since = Date.parse(sinceIso)
  if (Number.isNaN(since)) return null
  return Math.max(0, Math.floor((nowMs - since) / HOUR_MS))
}

export function formatWaiting(hours: number): string {
  if (hours < 24) return `há ${hours}h`
  const days = Math.floor(hours / 24)
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`
}

export const isWaitingOverdue = (hours: number | null): boolean =>
  hours !== null && hours > WAITING_OVERDUE_HOURS

/**
 * Rodapé do card no hub ("mais antigo há 4 dias"), a partir do `oldestWaitingSince`
 * do summary. Mesma régua da fila: destaque acima de 72h (D23). `null` = fila vazia.
 */
export function oldestWaitingFooter(
  sinceIso: string | null,
  nowMs = Date.now()
): { text: string; overdue: boolean } | null {
  const hours = waitingHours(sinceIso, nowMs)
  if (hours === null) return null
  return { text: `mais antigo ${formatWaiting(hours)}`, overdue: isWaitingOverdue(hours) }
}

/**
 * Início do plantão na hora local DELE (o ISO do backend traz o offset do plantão, R2.3):
 * "sáb 10/10 · 01:00". Sem offset legível (ex.: `Z`), lê em UTC; ISO inválido → "-".
 */
export function formatStationStart(iso: string): string {
  const offset =
    /[+-]\d{2}:?\d{2}$/.exec(iso)?.[0] ?? (iso.endsWith('Z') ? '+00:00' : DEFAULT_UTC_OFFSET)
  const weekday = formatWeekdayShortInOffset(iso, offset)
  const dayMonth = formatDayMonthInOffset(iso, offset)
  const time = formatHourMinuteInOffset(iso, offset)
  return weekday && dayMonth && time ? `${weekday} ${dayMonth} · ${time}` : '-'
}

// --- decisões -----------------------------------------------------------------

export type DecisionVerb = 'aprovado' | 'reprovado'

const inflect = (count: number, verb: DecisionVerb) => (count === 1 ? verb : `${verb}s`)

/**
 * Toast depois de aprovar/reprovar (1 ou lote). Falha parcial vira aviso com as contagens e a
 * primeira mensagem do backend (Review Focus 2): "1 aprovado, 1 não pôde ser aprovado: …".
 */
export function decisionSummary(
  result: BulkDecisionResponse,
  verb: DecisionVerb
): { tone: 'success' | 'warning'; message: string } {
  const ok = result.succeeded.length
  const failed = result.failed.length
  if (!failed) {
    return {
      tone: 'success',
      message: `${ok} ${ok === 1 ? 'documento' : 'documentos'} ${inflect(ok, verb)}`
    }
  }
  const couldNot = failed === 1 ? 'não pôde' : 'não puderam'
  return {
    tone: 'warning',
    message: `${ok} ${inflect(ok, verb)}, ${failed} ${couldNot} ser ${inflect(failed, verb)}: ${result.failed[0].message}`
  }
}

// --- aprovar ------------------------------------------------------------------

/** "dd/mm/aaaa" → "aaaa-mm-dd"; `null` quando a data não existe (ex.: 31/02). */
export function parseBrDate(value: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim())
  if (!match) return null
  const [, dd, mm, yyyy] = match
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)))
  const exists =
    date.getUTCFullYear() === Number(yyyy) &&
    date.getUTCMonth() === Number(mm) - 1 &&
    date.getUTCDate() === Number(dd)
  return exists ? `${yyyy}-${mm}-${dd}` : null
}

/** Hoje em America/Sao_Paulo ("aaaa-mm-dd"): a regra de vencimento é desse fuso (D10). */
export function todayInSaoPaulo(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now)
}

export interface ApproveFormItem {
  versionId: number
  hasExpiry: boolean
  acceptsAuthenticityCode: boolean
  /** "dd/mm/aaaa" digitado. */
  expiresAt: string
  authenticityCode: string
}

export const EXPIRY_ERROR = 'Informe uma data a partir de hoje no formato dd/mm/aaaa'

/**
 * Erros por `versionId`. Só tipo com validade exige data, de hoje em diante: o documento
 * vence em D+1 (D10), igual à regra do backend (`expiresAt < hoje` → 422).
 */
export function validateApproveForm(
  items: ApproveFormItem[],
  today: string
): Record<number, string> {
  const errors: Record<number, string> = {}
  for (const item of items) {
    if (!item.hasExpiry) continue
    const iso = parseBrDate(item.expiresAt)
    if (!iso || iso < today) errors[item.versionId] = EXPIRY_ERROR
  }
  return errors
}

export function buildApprovePayload(item: ApproveFormItem): ApproveVersionPayload {
  const payload: ApproveVersionPayload = {}
  const expiresAt = item.hasExpiry ? parseBrDate(item.expiresAt) : null
  if (expiresAt) payload.expiresAt = expiresAt
  const code = item.authenticityCode.trim()
  if (item.acceptsAuthenticityCode && code) payload.authenticityCode = code
  return payload
}

// --- reprovar -----------------------------------------------------------------

export interface RejectForm {
  reasons: ProfessionalDocumentRejectionReason[]
  otherReason: string
  guidance: string
}

export const REJECT_ERRORS = {
  reasons: 'Selecione ao menos um motivo.',
  other: 'Descreva o motivo para "Outro".'
} as const

export function validateRejectForm(form: RejectForm): { reasons?: string; other?: string } {
  if (!form.reasons.length) return { reasons: REJECT_ERRORS.reasons }
  const isOther = form.reasons.includes(ProfessionalDocumentRejectionReason.OTHER)
  if (isOther && !form.otherReason.trim()) return { other: REJECT_ERRORS.other }
  return {}
}

export function buildRejectPayload(form: RejectForm): RejectVersionPayload {
  const payload: RejectVersionPayload = { reasons: [...form.reasons] }
  if (form.reasons.includes(ProfessionalDocumentRejectionReason.OTHER)) {
    payload.otherReason = form.otherReason.trim()
  }
  const guidance = form.guidance.trim()
  if (guidance) payload.guidance = guidance
  return payload
}

// --- Kanban -------------------------------------------------------------------

export const KANBAN_TEST_ID = 'pre-allocation-kanban'

export interface PreAllocationColumnDef {
  key: PreAllocationColumn
  title: string
  tone: QueueTone
  hint: string
}

export const PRE_ALLOCATION_COLUMN_LABEL: Record<PreAllocationColumn, string> = {
  [PreAllocationColumn.AWAITING_PROFESSIONAL]: 'Aguardando médico',
  [PreAllocationColumn.IN_REVIEW]: 'Em análise',
  [PreAllocationColumn.RESOLVED]: 'Resolvido'
}

export const PRE_ALLOCATION_COLUMNS: PreAllocationColumnDef[] = [
  {
    key: PreAllocationColumn.AWAITING_PROFESSIONAL,
    title: PRE_ALLOCATION_COLUMN_LABEL[PreAllocationColumn.AWAITING_PROFESSIONAL],
    tone: 'crit',
    hint: 'Algum documento Pendente: nunca enviado, reprovado ou vencido.'
  },
  {
    key: PreAllocationColumn.IN_REVIEW,
    title: PRE_ALLOCATION_COLUMN_LABEL[PreAllocationColumn.IN_REVIEW],
    tone: 'warn',
    hint: 'Tudo enviado; falta a decisão do Time de Documentação.'
  },
  {
    key: PreAllocationColumn.RESOLVED,
    title: PRE_ALLOCATION_COLUMN_LABEL[PreAllocationColumn.RESOLVED],
    tone: 'ok',
    hint: 'Documentos Concluídos: a pré-alocação virou definitiva.'
  }
]

export const sortPreAllocationCards = (cards: PreAllocationCard[]): PreAllocationCard[] =>
  [...cards].sort((a, b) => a.hoursToFirstStation - b.hoursToFirstStation)

export function formatDurationHours(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}min`
  const days = Math.floor(hours / 24)
  const rest = Math.floor(hours % 24)
  if (days === 0) return `${rest}h`
  return rest ? `${days}d ${rest}h` : `${days}d`
}

export type CountdownTone = 'crit' | 'warn' | 'neutral'

/** Contagem até o plantão mais próximo; nada no Resolvido. ≤ 24h = crítico (T-24h). */
export function countdown(card: PreAllocationCard): { label: string; tone: CountdownTone } | null {
  if (card.column === PreAllocationColumn.RESOLVED) return null
  const hours = card.hoursToFirstStation
  if (hours <= 0) return { label: 'plantão começou', tone: 'crit' }
  const tone: CountdownTone = hours <= 24 ? 'crit' : hours <= 72 ? 'warn' : 'neutral'
  return { label: `faltam ${formatDurationHours(hours)}`, tone }
}

export type PreAllocationStation = PreAllocationCard['stations'][number]

/** Plantões do cartão do mais próximo ao mais distante (o primeiro é o da contagem regressiva). */
export const stationsByStart = (card: PreAllocationCard): PreAllocationStation[] =>
  [...card.stations].sort((a, b) => Date.parse(a.start) - Date.parse(b.start))

/**
 * Os 3 tempos do D29. `null` do backend = ainda não dá para medir (o médico não enviou tudo
 * depois da pré-alocação, ou nem tudo foi decidido). No Resolvido o que faltou medir vira "—".
 */
export function preAllocationTimeLabels(card: PreAllocationCard): {
  completion: string
  decision: string
  waiting: string
} {
  const { professionalCompletionHours: completion, submissionToDecisionHours: decision } =
    card.times
  const awaiting = card.column === PreAllocationColumn.AWAITING_PROFESSIONAL
  const resolved = card.column === PreAllocationColumn.RESOLVED
  let decisionLabel = 'em curso'
  if (decision !== null) decisionLabel = formatDurationHours(decision)
  else if (resolved) decisionLabel = '—'
  else if (awaiting && completion === null) decisionLabel = 'sem envio'
  return {
    completion: completion !== null ? formatDurationHours(completion) : awaiting ? 'em curso' : '—',
    decision: decisionLabel,
    waiting: formatDurationHours(card.times.waitingForOkHours)
  }
}

/**
 * A coluna é derivada (D38): só pergunta ao backend quando o destino pode mudar algo. Cartão
 * Resolvido e volta para "Aguardando médico" o backend aceita sem fazer nada (204).
 */
export const shouldRequestMove = (card: PreAllocationCard, target: PreAllocationColumn): boolean =>
  card.column !== target &&
  card.column !== PreAllocationColumn.RESOLVED &&
  target !== PreAllocationColumn.AWAITING_PROFESSIONAL

const COLUMN_KEYS = Object.values(PreAllocationColumn) as string[]

/** `pre-allocation-kanban-column-<KEY>[-hint|-load-more]` → `<KEY>` (o QueueKanban gera esses ids). */
export function parseKanbanColumnKey(testId: string | null): PreAllocationColumn | null {
  const prefix = `${KANBAN_TEST_ID}-column-`
  if (!testId?.startsWith(prefix)) return null
  const key = testId.slice(prefix.length).split('-')[0]
  return COLUMN_KEYS.includes(key) ? (key as PreAllocationColumn) : null
}

/** Limite do `search` da fila no backend (acima disso, 400). */
const REVIEW_SEARCH_MAX = 100

/**
 * Busca da aba Documentos para o médico de um cartão do Kanban ("Revisar documentos", R32).
 * O backend compara o nome ou o número do CRM, sem a UF: o cartão traz "123456/SP".
 */
export function reviewSearchFor(
  professional: Pick<PreAllocationCard['professional'], 'name' | 'crm'>
): string {
  const crmNumber = professional.crm?.split('/')[0].trim()
  return (crmNumber || professional.name.trim()).slice(0, REVIEW_SEARCH_MAX)
}
